import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { Cell, H4, Row, Section, StatusPayment, Tag, TooltipIcon, TooltipWrapper } from 'components';
import { Loadable } from 'components/types';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { AchMethod, AchResponseType, AchStatus, IAchTransaction, IAlert, PayoutStatus } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { capitalize } from 'utils/strings';

// RTH = ReportTableHeader
const RTHLayout = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(theme.space.xl)};
  gap: ${toRem(theme.space.md)};
`;

const RTHTitleLayout = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: baseline;
  width: auto;

  ${H4} {
    strong {
      font-weight: ${theme.font.weight.semiBold};
    }
  }
`;

const RTHActions = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  gap: ${toRem(theme.space.md)};
`;

interface IReportTableHeaderProps extends Loadable {
  actions?: ReactNode;
  count?: number;
  title: string;
}

export const ReportTableHeader = ({ actions, count, isLoading, title = 'Results' }: IReportTableHeaderProps) => (
  <RTHLayout>
    <RTHTitleLayout data-cy="report-table-header">
      {isLoading || isNil(count) ? (
        <Skeleton width={55} />
      ) : (
        <H4>
          <strong>{title}</strong> ({count || 0})
        </H4>
      )}
    </RTHTitleLayout>

    {actions && <RTHActions>{actions}</RTHActions>}
  </RTHLayout>
);

// Payroll Tips
// The frame's 211px plus room for the Select's chevron handle, so a label and its lock fit.
export const PayrollPeriodWrapper = styled.div`
  display: flex;
  width: ${toRem(230)};

  .react-dropdown-select-dropdown {
    white-space: nowrap;
  }
`;

// Bank Reconciliation
export const BRSection = styled(Section)`
  z-index: 10;
  padding-bottom: 0;

  > div {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export const BRSummaryRow = styled.div`
  display: grid;
  width: 100%;
  grid-gap: ${toRem(theme.space.xl)};
  grid-template-columns: 1fr;
  padding: ${toRem(theme.space.xl)} ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.small}px) {
    grid-template-columns: repeat(auto-fit, minmax(${toRem(200)}, 1fr));
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(theme.space.xl)} 0;
    min-height: ${toRem(110)};
    padding: ${toRem(theme.space.xl)} 0;
  }
`;

const BRCardLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
  padding-right: ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.med}px) {
    border-right: ${toRem(1)} solid ${theme.color.neutral[200]};

    &:last-child {
      border-right: none;
    }
  }
`;

const BRCardInner = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    justify-content: center;
  }
`;

interface IBRCardProps {
  children: ReactNode;
}

export const BRCard = ({ children }: IBRCardProps) => (
  <BRCardLayout>
    <BRCardInner>{children}</BRCardInner>
  </BRCardLayout>
);

export const BRMultiTableLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(theme.space.xl)};
  padding: ${toRem(theme.space.xl)};
`;

const StyledSkeleton = styled(Skeleton)`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  width: 100%;
  text-align: right;

  span {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    text-align: right;
  }
`;

const Column = styled.div`
  flex-flow: column;
  width: 100%;
  gap: ${toRem(theme.space['2xs'])};
`;

interface IUserPayoutRowProps {
  gross: string;
  holdback: string;
  net: string;
  autograts: string;
  tips: string;
  alert?: IAlert;
  businessDay: string;
  dateSubmitted: string;
  isLoading?: boolean;
  onClick: () => void;
  payoutId: string;
  sentBy: string;
  site?: string;
  status: PayoutStatus;
  timeSubmitted: string;
}

export const UserPayoutRow = ({ alert, gross, net, holdback, autograts, tips, businessDay, isLoading, onClick, payoutId, status, ...rest }: IUserPayoutRowProps) => (
  <Row clickable onClick={onClick} {...rest}>
    <Cell minWidth={110} data-cy="business-day-cell">
      <Column>
        {isLoading ? <StyledSkeleton width={110} /> : <strong>{businessDay}</strong>}
        {isLoading ? <StyledSkeleton width={110} /> : payoutId}
      </Column>
    </Cell>

    <Cell minWidth={110} data-cy="status-cell">
      <StatusPayment
        isLoading={isLoading}
        status={status}
        paymentId={payoutId}
        {...(alert && {
          subStatusLabel: alert.label,
          subStatusTooltip: alert.message,
        })}
      />
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tips-cell">
      {isLoading ? <Skeleton width={110} /> : tips}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="autograts-cell">
      {isLoading ? <Skeleton width={110} /> : autograts}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tip-gross-cell">
      {isLoading ? <Skeleton width={110} /> : gross}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tip-holdback-cell">
      {isLoading ? <Skeleton width={110} /> : holdback}
    </Cell>

    <Cell minWidth={110} align="left" data-cy="tip-net-cell">
      {isLoading ? <Skeleton width={110} /> : <strong>{net}</strong>}
    </Cell>
  </Row>
);

const getStatusMessage = (status: AchStatus) => {
  switch (status) {
    case AchStatus.APPLIED:
      return 'This ACH is complete and has been applied to the site ledger.';
    case AchStatus.CANCELED:
      return 'This ACH was canceled.';
    case AchStatus.PENDING:
      return 'This ACH will be processed on the next bank business day.';
    case AchStatus.PROCESSED:
      return 'This ACH has been submitted to your bank.';
    case AchStatus.PROCESSING:
      return 'The system is currently working on this ACH.';
    case AchStatus.RETURNED:
      return 'This ACH failed. Click on the ACH to see the failure reason.';
    default:
      return capitalize(status);
  }
};

const Content = styled.div`
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

interface IAchTransactionRowProps {
  transaction?: IAchTransaction;
  isLoading?: boolean;
  onClick?: () => void;
}

export const AchTransactionRow = ({ transaction, isLoading, onClick, ...rest }: IAchTransactionRowProps) => (
  <Row clickable onClick={onClick} {...rest} data-value={transaction?.id}>
    <Cell maxWidth={120} align="left" data-cy="date-cell">
      {isLoading ? <Skeleton width={100} /> : <b>{dayjs(transaction?.createdAt).format('MM-DD-YYYY')}</b>}
    </Cell>

    <Cell maxWidth={120} align="left" data-cy="id-cell">
      {isLoading ? (
        <Skeleton width={100} />
      ) : (
        <Content>
          {transaction?.id} {transaction?.achResponse?.type === AchResponseType.CHANGE_REQUEST && <Tag data-cy="noc-message" className="noc-message" appearance={Appearance.WARNING} label="NOC" size={Size.SMALL} />}
        </Content>
      )}
    </Cell>

    <Cell minWidth={170} maxWidth={200} align="left" wrapText data-cy="desc-cell">
      {isLoading ? <Skeleton width={160} /> : transaction?.method === AchMethod.AUTO ? 'Automatic ACH' : transaction?.description || '--'}
    </Cell>

    <Cell align="left" data-cy="type-cell">
      {isLoading ? <Skeleton width={100} /> : capitalize(transaction?.paymentType)}
    </Cell>

    <Cell minWidth={100} data-cy="status-cell">
      {isLoading ? (
        <Skeleton width={100} />
      ) : (
        <TooltipWrapper data-cy="tooltip-wrapper">
          {capitalize(transaction?.status)}
          <TooltipIcon id={`tooltip-${transaction?.id}`} place="top">
            {getStatusMessage(transaction?.status)}
          </TooltipIcon>
        </TooltipWrapper>
      )}
    </Cell>

    <Cell align="left" data-cy="amount-cell">
      {isLoading ? <Skeleton width={100} /> : <b>{centsToDollarsDisplay(transaction?.amount)}</b>}
    </Cell>
  </Row>
);
